/* eslint react-hooks/refs: ["warn"] */
import { useToastNotification } from "@coworker/components/src/components/ToastNotification";
import { Trans } from "@coworker/locales";
import React, { useMemo } from "react";
import styled, { css } from "styled-components";
import ArrowIcon from "../../assets/svg/arrow-small-forward.svg?react";
import {
  useWorkspacesAction,
  useWorkspacesState,
} from "../../hooks/useWorkspaces";
import AddQuestionInput from "./AddQuestionInput";
import Instructions from "../Instructions";
import {
  describeAreaFilter,
  getAreaFilter,
} from "../InputPopup/UserFiltersRow/filterChoices";
import profilePreferences from "@coworker/enums/profilePreferences";
import { useInputPopup } from "../InputPopup";
import tracker from "../../helpers/tracker";
import { useStoreId } from "../../core/auth/useLoggedInUser";
import { createQuestion } from "./hooks/useMFAQsService";
import FullscreenPopup from "@coworker/apprestructured/src/layout/components/FullScreenPopup/FullScreenPopup";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { useTranslation } from "@coworker/locales";
import Linkbar from "@coworker/apprestructured/src/shared/components/Linkbar/Linkbar";
import { useMfaqFilters } from "./hooks/useMfaqFilters";
import { DEFUALT_PERIOD_VALUE, PERIOD_ID } from "./hooks/usePeriodFilter";
import { DEFUALT_LOCATION_VALUE, LOCATION_ID } from "./hooks/useLocationFilter";
import { getSelectedFilterValue } from "@coworker/apprestructured/src/shared/filters/utils/filter/filtervalue";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate.js";

const { DISABLE_POPUPS_MFAQ_ADD_QUESTION } = profilePreferences;

const Block = styled.div`
  padding: 0 24px;
  margin: 25px 0;

  &:not(:last-child) {
    border-bottom: 1px solid var(--grey100);
  }

  &:last-child {
    margin-bottom: 87px;
  }

  & > span {
    height: 50px;
    font-size: 18px;
  }
`;

const AreaInput = styled.div`
  height: 92px;
  font-size: 14px;
  ${({ isSelected }) =>
    isSelected
      ? css`
          color: var(--grey900);
        `
      : css`
          color: var(--grey600);
        `}
  display: flex;
  flex-direction: row;
  align-items: center;

  & > span {
    flex-grow: 1;
  }

  & > svg {
    flex-grow: 0;
  }
`;

const QuestionTypePillsWrapper = styled.div`
  margin: 30px 0;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: left;

  & > * {
    margin-bottom: 10px;
  }
`;

const NewQuestion = () => {
  const { navigationState } = useWorkspacesState();
  const { setNavigationState } = useWorkspacesAction();
  const navigate = useFixaNavigate();

  const store_id = useStoreId();

  const mfaqFilters = useMfaqFilters({ hideQuestionsWithTasksOnly: false });

  const period = getSelectedFilterValue(
    PERIOD_ID,
    mfaqFilters.filterValues,
    DEFUALT_PERIOD_VALUE
  ).value;

  const location = getSelectedFilterValue(
    LOCATION_ID,
    mfaqFilters.filterValues,
    DEFUALT_LOCATION_VALUE
  ).value;

  const isWholeStore = location === DEFUALT_LOCATION_VALUE.value;
  const [selectedArea, setSelectedAreaState] = React.useState(
    navigationState?.selectedArea || (isWholeStore ? null : `${location}`)
  );
  const { getInput } = useInputPopup();
  const { showToast } = useToastNotification();

  const questionDataRef = React.useRef({
    store_id,
    type: "",
    area: selectedArea,
    question_text: "",
    subject: "",
  });
  const setSelectedArea = (area) => {
    setNavigationState({ ...navigationState, selectedArea: area });
    setSelectedAreaState(area);
    questionDataRef.current.area = area;
  };
  const [canCreateQuestion, setCanCreateQuestion] = React.useState(
    !!selectedArea && !!questionDataRef.current?.subject?.trim()
  );

  React.useEffect(() => {
    setCanCreateQuestion(
      !!selectedArea && !!questionDataRef.current?.subject?.trim()
    );
  }, [selectedArea]);

  const onSubmit = React.useCallback(async () => {
    if (canCreateQuestion) {
      await createQuestion(
        questionDataRef.current.store_id,
        questionDataRef.current.area || location,
        questionDataRef.current.question_text
      );
      tracker.MFAQ.trackCreateQuestion(questionDataRef.current);
      navigate("/customerquestions");
      showToast(<Trans>questionSucessfullyCreatedString</Trans>);
    }
  }, [canCreateQuestion, navigate, showToast, location]);

  const { t } = useTranslation();

  const linkbarItems = useMemo(
    () => [
      {
        label: t("customerQuestionsString"),
        to: "/customerquestions",
      },
    ],
    [t]
  );

  /**
   * @type {React.MutableRefObject<HTMLDivElement}
   */
  const pillsWrapperRef = React.useRef();
  React.useEffect(() => {
    if (selectedArea && pillsWrapperRef.current) {
      const inputElement = pillsWrapperRef.current
        .getElementsByTagName("input")
        .item(0);
      inputElement.focus();
      // This is causing some issues with css. Entire page shifts upp and styles is messing. Don't see the need to scroll since its just below Area and focusing should be enough
      /* inputElement.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });*/
    }
  }, [selectedArea]);

  return (
    <FullscreenPopup
      noPadding
      isActionBarHiddenOnScroll={false}
      actionBarContent={
        <FixaButton
          data-testid="submitNewQuestion"
          disabled={!canCreateQuestion}
          type="primary"
          text={<Trans>submitString</Trans>}
          onClick={onSubmit}
        />
      }
      linkbar={
        <Linkbar
          items={linkbarItems}
          currentLabel={t("addQuestionTitle")}
          currentAsH1
        />
      }
    >
      <Instructions
        title={<Trans>mfaqSelectRightDetailsString</Trans>}
        text={<Trans>mfaqAddQuestionInstructions1String</Trans>}
        type={DISABLE_POPUPS_MFAQ_ADD_QUESTION}
        showBottomBorder
      />
      <Block
        data-testid="selectArea-popup"
        onClick={() => {
          getInput("userFilters", {
            title: <Trans>whereWasQuestionAskedString</Trans>,
            actionText: <Trans>selectLocationString</Trans>,
            hidePeriodFilters: true,
            hideWholeStore: true,
            noPersist: false,
            hideShowTasksToggle: true,
            onSetFilters: (newFilters) => {
              setSelectedArea(getAreaFilter(newFilters));
              setCanCreateQuestion(!!questionDataRef.current?.subject?.trim());
            },
            filters: [selectedArea ?? location, period],
          });
        }}
      >
        <span>
          <strong>
            <Trans>location3String</Trans>
          </strong>
        </span>
        <AreaInput isSelected={!!selectedArea}>
          <span>
            {selectedArea ? (
              describeAreaFilter([selectedArea])
            ) : (
              <Trans>whereWasQuestionAskedString</Trans>
            )}
          </span>
          <ArrowIcon />
        </AreaInput>
      </Block>
      <Block>
        <span>
          <strong>
            <Trans>questionStringMFAQ</Trans>
          </strong>
        </span>
        <QuestionTypePillsWrapper ref={pillsWrapperRef}>
          <AddQuestionInput
            questionDataRef={questionDataRef}
            onInputTextChange={(text) =>
              setCanCreateQuestion(!!selectedArea && text?.trim().length >= 5)
            }
            areaIsWholeStore={isWholeStore && !selectedArea}
            selectedArea={selectedArea}
          />
        </QuestionTypePillsWrapper>
      </Block>
    </FullscreenPopup>
  );
};

export default NewQuestion;
